import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import {AdtaDeviceOverviewContext} from "../context/AdtaDeviceOverviewContext";
import Alert from "@amzn/meridian/alert"
import {
    MAC_MASK,
    PORT_MASK,
    isValidEntry,
    IP_ADDRESS_PATTERN,
    PATTERNS, validateNewAdtaDevice, PROTOCOLS,
} from "../../../util/Validations";
import {assetTypeToConfigTypeDict} from "../../../util/Util";

const AddAdtaSingleDeviceModal = (props) => {

    const {formatMessage} = props.intl;
    const {open, setOpen, selected, setSelected} = props;
    const {viewModel, dispatch} = useContext(AdtaDeviceOverviewContext);

    // Visual controls
    const [showAlert, setShowAlert] = useState(false)

    // States
    const [port, setPort] = useState("")
    const [deviceType, setDeviceType] = useState("")
    const [name, setName] = useState("")
    const [ip, setIp] = useState("")
    const [mac, setMac] = useState("")
    const [id, setId] = useState("")

    // Error
    const [portErr, setPortErr] = useState(false)
    const [deviceTypeErr, setDeviceTypeErr] = useState(false)
    const [nameErr, setNameErr] = useState(false)
    const [ipErr, setIpErr] = useState(false)
    const [macErr, setMacErr] = useState(false)
    const [idErr, setIdErr] = useState(false)

    const stateValues = {
        mac: mac,
        ip: ip,
        name: name,
        deviceType: deviceType,
        port: port,
        deviceConfig: {
            type: assetTypeToConfigTypeDict.typeADTA,
            id: id
        }
    }
    const onCloseModal = () => {
        setOpen(false)
        resetInputState();
        setShowAlert(false);
    }
    const onClickConfirm = (stateValues) => {
        if (validateNewAdtaDevice(stateValues)) {
            setShowAlert(false)
            stateValues.deviceConfig = JSON.stringify(stateValues.deviceConfig)
            if (selected) {
                stateValues.sorterConfig = JSON.stringify(viewModel.sorterConfig[stateValues.mac]);
                stateValues.sorterTestPackage = JSON.stringify(viewModel.sorterTestPackageConfig[stateValues.mac]);
                updateData(stateValues);
            } else {
                addData(stateValues);
            }
            resetInputState();
            setOpen(false);
        } else {
            setShowAlert(true)
        }
    }

    function resetInputState() {
        // setting state of input fields as empty to have empty inputs on clicking Add Device again
        setMac("");
        setMacErr(false);
        resetAssetDetails();
        resetAssetDetailErrors();
    }

    const resetAssetDetails = () => {
        setIp("");
        setId("");
        setPort("");
        setDeviceType("");
        setName("");
    }

    const resetAssetDetailErrors = () => {
        setIpErr(false);
        setIdErr(false);
        setPortErr(false);
        setDeviceTypeErr(false);
        setNameErr(false);
    }

    const prefill = (value) => {
        if (viewModel.adtaDeviceInformation.hasOwnProperty(value)) {
            setSelected(value);
            const device = viewModel.adtaDeviceInformation[value]
            resetAssetDetailErrors();
            setMac(device.mac)
            setIp(device.ip);
            setId(device.id);
            setPort(device.port);
            setDeviceType(device.deviceType);
            setName(device.name);
        } else if (selected) {
            setSelected("");
            resetAssetDetails();
        }
    }

    const addData = (stateValues) => {
        dispatch({type: 'ADD_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues}})
    }
    const updateData = (stateValues) => {
        dispatch({type: 'UPDATE_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues}})
    }

    return (
        <Modal
            title={formatMessage({id: "device.add_device_button_modal"})}
            open={open}
            onOpen={() => prefill(selected)}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium"
            width="200%">
            <Column spacing={"medium"} spacingInset={"medium"}>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="mac-input">
                        <FormattedMessage id="modal.mac" defaultMessage="MAC Address"/>
                    </Text>
                    <Input id="mac-input"
                        value={mac}
                        onChange={setMac}
                        mask={MAC_MASK}
                        onBlur={() => {
                            setMacErr(!isValidEntry(stateValues.mac, PATTERNS.MAC))
                            prefill(mac);
                        }}
                        error={macErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="ip-input">
                        <FormattedMessage id="modal.ip" defaultMessage="IP Address"/>
                    </Text>
                    <Input id="ip-input"
                        value={ip}
                        onChange={setIp}
                        onBlur={() => setIpErr(!isValidEntry(stateValues.ip, IP_ADDRESS_PATTERN))}
                        error={ipErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="port-input">
                        <FormattedMessage id="modal.port" defaultMessage="Port"/>
                    </Text>
                    <Input id="port-input"
                           value={port}
                           onChange={setPort}
                           mask={PORT_MASK}
                           onBlur={() => setPortErr(!isValidEntry(stateValues.port, PATTERNS.BAY_ID))}
                           error={portErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        <FormattedMessage id="modal.protocol"
                                          defaultMessage="Protocol"/>
                    </Text>
                    <Input id="device-type-input"
                           value={deviceType}
                           onChange={setDeviceType}
                           onBlur={() => {
                               setDeviceTypeErr(!PROTOCOLS.ADTA.includes(stateValues.deviceType)
                                   && stateValues.deviceType !== '')
                           }}
                           error={deviceTypeErr}
                           helperText={"Either \"sort\" or \"sort-2021-v1\""}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="name-input">
                        <FormattedMessage id="modal.name"
                                          defaultMessage="Device Name"/>
                    </Text>
                    <Input id="name-input"
                        value={name}
                        onChange={setName}
                        onBlur={() => setNameErr(!isValidEntry(stateValues.name, PATTERNS.NAME))}
                        error={nameErr}
                        helperText={"Display name of the device. e.g. \"Finger A\""}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="id-input">
                        <FormattedMessage id="modal.device_id"
                                          defaultMessage="Device ID"/>
                    </Text>
                    <Input id="id-input"
                        value={id}
                        onChange={setId}
                        onBlur={() => setIdErr(!isValidEntry(stateValues.deviceConfig.id, PATTERNS.DEVICE_ID))}
                        error={idErr}
                        helperText={"Unique local identifier for the device. eg. \"A\" for \"Finger A\""}
                    />
                </Column>
            </Column>
            <ModalFooter>
                <Column>
                    {showAlert && <Alert type="warning" size="small">
                        <FormattedMessage id="alert.missingfields"
                                          defaultMessage="Please enter all fields in correct format!"/>
                    </Alert>}
                </Column>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="medium" onClick={onCloseModal}>
                        <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                    </Button>
                    <Button type="primary"
                            size="medium"
                            onClick={() => onClickConfirm(stateValues)}>
                        {selected ? <FormattedMessage id="modal.confirmEdit" defaultMessage="Confirm edit"/> :
                            <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>
                        }
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    )
}

export default injectIntl(AddAdtaSingleDeviceModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/adtadevice/modal/AddAdtaSingleDeviceModal.js